'use client';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { ShippingAddress } from 'shared/types/account/Account';
import { useAccount } from '@/frontastic';
import { useShowActiveBrandSection } from '@/frontastic/hooks/useShowActiveBrandSection';
import { getCookieValue } from '@/helpers/utils/siteUtils';
import { CustomerAddressPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { COOKIE_KEYS } from '../../../../../../constants/constant';
import AddNewAddressModal from '../../../modals/customerDetailAction/addNewAddress';
import EditAddressModal from '../../../modals/customerDetailAction/editAddress';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../ui/customAccordian/customAccordian';
import { Table, TableColumnConfig } from '../../../ui/customTable/Table';
import Loader from '../../../ui/loader';
import Pagination from '../../../utils/pagination';
import {
  PAYLOAD_QUERY,
  RESPONSE_SUCCESS_MESSAGE,
  SHIPPING_ADDRESS_ITEM_PER_PAGE,
  SHIP_ADDRESS_HEADERS,
  SHIP_ADDRESS_KEYS,
} from '../../constants';
import { ShippingAddressDetailsType } from '../../types';
import { transformShippingAddress } from '../../utils';
const CustomerShippingAddress = ({ Texts }: { Texts: any }) => {
  const [shippingAddress, setShippingAddress] = useState<ShippingAddressDetailsType[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [rowData, setRowData] = useState<ShippingAddress>();
  const [loading, setLoading] = useState(false);
  const { getCustomerAddresses } = useAccount();
  const params = useSearchParams();
  const [value, setValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const startOrder = (currentPage - 1) * SHIPPING_ADDRESS_ITEM_PER_PAGE + 1;
  const endOrder = Math.min(startOrder + SHIPPING_ADDRESS_ITEM_PER_PAGE - 1, totalItems);
  const showActiveBrandSection = useShowActiveBrandSection();

  const shippingAddressHeaders: TableColumnConfig<any>[] = [
    {
      header: SHIP_ADDRESS_HEADERS.NAME,
      key: 'FULL-NAME',
      render: (data, rowData) => (
        <div className="gbmask">
          {rowData?.firstName} {rowData?.lastName}
        </div>
      ),
    },
    {
      header: SHIP_ADDRESS_HEADERS.ADDRESS,
      key: SHIP_ADDRESS_KEYS.LINE1,
      render: (data, rowData) => (
        <div className="gbmask flex flex-col">
          <p>
            {rowData?.streetName} {rowData?.city}
          </p>
          <p>{rowData?.additionalAddressInfo}</p>
          <p>
            {rowData.state}, {rowData.postalCode}
          </p>
        </div>
      ),
    },
    {
      header: SHIP_ADDRESS_HEADERS.DEFAULT_SHIPPING,
      key: '',
      //render: (data, rowData, rowIndex) => <div>{rowIndex === 0 && currentPage === 1 ? <GreenTickIcon /> : ''}</div>,
      render: (data, rowData, rowIndex) => <div>{rowIndex === 0 && currentPage === 1 ? '' : ''}</div>,
    },
  ];

  if (showActiveBrandSection) {
    shippingAddressHeaders.push({
      header: SHIP_ADDRESS_HEADERS.ACTIONS,
      key: '',
      render: (data, rowData) => (
        <div>
          <button
            onClick={() => {
              setOpenEditModal(true);
              setRowData(rowData);
            }}
            className={`text-blueLight underline`}
          >
            {Texts.editButton}
          </button>
        </div>
      ),
    });
  }

  const updateAddress = (addressId: string, updatedAddress: any) => {
    const updateAddress = shippingAddress?.map((item) => {
      if (item.addressId === addressId) {
        return {
          ...item,
          fullName: `${updatedAddress?.firstName} ${updatedAddress?.lastName}`,
          ...updatedAddress,
        };
      }
      return item;
    });
    setShippingAddress(updateAddress);
  };

  const AddNewAddress = (newAddress: any) => {
    // Find the last addressId
    const lastAddressId = shippingAddress?.reduce((maxId, item) => {
      const currentId = parseInt(item.addressId, 10);
      return currentId > maxId ? currentId : maxId;
    }, 0);

    const newAddressWithId = {
      ...newAddress,
      fullName: `${newAddress?.firstName} ${newAddress?.lastName}`,
      addressId: (lastAddressId + 1).toString(),
    };

    const updatedData = [...shippingAddress, newAddressWithId]?.slice(0, 5);

    setShippingAddress(updatedData);
  };

  const getAddresses = async (pageNumber?: number) => {
    setLoading(true);
    const payload = {
      query: {
        omsCustomerId: params.get(PAYLOAD_QUERY.OMS_CUSTOMER_ID),
        pageNumber: pageNumber ?? currentPage,
        pageSize: SHIPPING_ADDRESS_ITEM_PER_PAGE,
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
    } as CustomerAddressPayload;
    await getCustomerAddresses(payload).then((response) => {
      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        const transformedAddress = transformShippingAddress(response?.RecipientAddresses || []);
        setShippingAddress(transformedAddress);
        setTotalItems(response?.quantity ?? 0);
      }
    });
    setLoading(false);
  };
  useEffect(() => {
    if (value && shippingAddress?.length === 0) {
      getAddresses();
    }
  }, [value]);

  const onPageChange = async (page: number) => {
    setCurrentPage(page);
    await getAddresses(page);
  };

  return (
    <Accordion
      type="single"
      collapsible
      className={`${
        value === 'shippingAddress' ? 'pb-0' : ''
      } w-full rounded-md border border-foggyGray px-5 py-1 shadow-lg`}
      onValueChange={setValue}
    >
      <AccordionItem value="shippingAddress" className="border-none">
        <AccordionTrigger className="text-lg font-bold hover:no-underline">
          <section className="flex flex-col items-start justify-start">
            <div>{Texts.shippingAddress}</div>
            {value === 'shippingAddress' && (
              <p className="mr-auto text-sm font-normal">
                Showing addresses {startOrder}-{endOrder} of {totalItems}
              </p>
            )}
          </section>
        </AccordionTrigger>
        <AccordionContent>
          {loading && <Loader />}
          {openEditModal && (
            <EditAddressModal
              Texts={Texts}
              data={rowData}
              updateAddress={updateAddress}
              open={openEditModal}
              setOpen={setOpenEditModal}
            />
          )}
          {showActiveBrandSection && (
            <section className="mb-4 flex justify-end">
              <button
                onClick={() => setOpenModal(true)}
                className="h-[30px] rounded-md border-[1px] border-foggyGray px-3 py-1 text-sm font-medium"
              >
                {Texts.addNewAddress}
              </button>
              {openModal && (
                <AddNewAddressModal
                  Texts={Texts}
                  AddNewAddress={AddNewAddress}
                  open={openModal}
                  setOpen={setOpenModal}
                  getAddresses={getAddresses}
                />
              )}
            </section>
          )}
          <Table columns={shippingAddressHeaders} data={shippingAddress}></Table>
          <div className="py-2">{shippingAddress?.length === 0 ? Texts.noAddressFound : ''}</div>
          {totalItems > 5 && (
            <section className="mt-4 flex w-full items-center justify-end">
              <Pagination
                currentPage={currentPage}
                itemsPerPage={SHIPPING_ADDRESS_ITEM_PER_PAGE}
                totalItems={totalItems}
                onPageChange={onPageChange}
              ></Pagination>
            </section>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
export default CustomerShippingAddress;
